import React, { useState, useEffect, useCallback } from 'react';
import {
  Button as ButtonM,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
  withStyles,
  Box,
  Divider
} from '@material-ui/core';
import MuiAlert, { AlertProps, type Color } from '@material-ui/lab/Alert';
import { Link, useParams } from 'react-router-dom';

import { api } from '../../../../services/api';
import { Autocomplete } from '@material-ui/lab';
import { Add, Check, Delete, GetApp, Publish, Save, ArrowBack, Info, CheckCircle } from '@material-ui/icons';
import { type AxiosResponse } from 'axios';
import { LoadingPage } from '@components/LoadingPage';
import { getFileName, sendFileWithoutFake, verifyUpLoads } from '@utils/masks';
import { ASO } from '@data/tipoAso';
import { useQuery } from '@tanstack/react-query';
import { getDataScheduling } from '@api/get-data-schedule';
import { LinkKitButton } from '@components/LinkKitButton';
import { Button } from '@components/Button';
import { LabelUpload } from './input';
import { Upload } from '@phosphor-icons/react';
import { Guia } from '../../PDF/gerarGuia';
import { PDF } from '../../PDF/gerarPdf';

interface IExames {
  id_exames: string;
  nome: string;
  valor: string;
}

const ButtonAdd = withStyles(() => ({
  root: {
    background: '#0D9C5B',
    color: '#FFF',
    padding: '10px 30px',
    fontWeight: 'bold',
    border: 'none',
    '&:hover': {
      background: '#0a7e49',
      color: '#FFF',
    },
    '&.Mui-disabled': {
      background: '#cccccc',
      color: '#666666'
    }
  }
}))(ButtonM);

const ButtonSair = withStyles(() => ({
  root: {
    background: '#f44336',
    color: '#FFF',
    padding: '10px 30px',
    fontWeight: 'bold',
    border: 'none',
    '&:hover': {
      background: '#d32f2f',
      color: '#FFF',
    }
  }
}))(ButtonM);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box mt={4} mb={2}>
    <Typography variant="h6" style={{ fontWeight: 'bold', color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
      <Info fontSize="small" color="primary" /> {children}
    </Typography>
    <Divider />
  </Box>
);

export const EditAgendamentoExterno: React.FC<any> = (props) => {
  const { id } = useParams<{ id: string }>();
  const { id_agendamento } = props.location.state;

  const [cpfColab, setCpfColab] = useState<string>('');
  const [nomeColab, setNomeColab] = useState<string>('');
  const [dataAgendamento, setDataAgendamento] = useState<string>('');
  const [tipoAso, setTipoAso] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState<boolean>(false);
  const [message, setMessage] = useState('');
  const [cod, setCod] = useState<number | null>(null);
  const [severity, setSeverity] = useState<Color>();
  const nome_user = localStorage.getItem('NameUser') || '';

  const [campoValor, setCampoValor] = useState<any[]>([]);
  const [dialog, setDialog] = useState<boolean>(false);
  const [singleExame, setSingleExame] = useState<IExames | null>(null);
  const [newData, setNewData] = useState<IExames[]>([]);

  const [arquivoProntuario, setArquivoProntuario] = useState<File | string>('');
  const [arquivoSendProntuario, setArquivoSendProntuario] = useState<File | string>('');
  const [arquivoASO, setArquivoASO] = useState<File | string>('');
  const [arquivoSendASO, setArquivoSendASO] = useState<File | string>('');

  // Estados para controle de alterações e salvamento
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  
  // Armazenar os dados originais
  const [originalData, setOriginalData] = useState({
    data_agendamento: '',
    tipo_aso: '',
    exames: [] as any[],
    arquivo_aso: '' as string,
    arquivo_prontuario: '' as string
  });

  const {
    data: getDataSchedule,
    isFetching
  } = useQuery({
    queryKey: ['getSchedule', id_agendamento],
    queryFn: () => getDataScheduling(id_agendamento)
  });

  // Função para normalizar o valor do arquivo (tratar "None" como vazio)
  const normalizeArquivo = (valor: string): string => {
    if (!valor || valor === 'None' || valor === 'null' || valor === 'undefined') {
      return '';
    }
    return valor;
  };

  // Carregar os dados iniciais
  useEffect(() => {
    if (getDataSchedule) {
      setCpfColab(getDataSchedule.cpf_colaborador ?? '');
      setNomeColab(getDataSchedule.nome ?? '');
      setDataAgendamento(getDataSchedule.data_agendamento ?? '');
      setTipoAso(getDataSchedule.tipo_aso ?? '');
      
      setOriginalData(prev => ({
        ...prev,
        data_agendamento: getDataSchedule.data_agendamento ?? '',
        tipo_aso: getDataSchedule.tipo_aso ?? '',
        arquivo_aso: normalizeArquivo(getDataSchedule.arquivo_aso || ''),
        arquivo_prontuario: normalizeArquivo(getDataSchedule.arquivo_prontuario || '')
      }));
      
      // Carregar exames
      carregarExames(getDataSchedule.cpf_colaborador, getDataSchedule.num_agenda);
    }
  }, [getDataSchedule]);

  // Função para carregar os exames
  const carregarExames = async (cpf: string, agenda: string) => {
    try {
      const res = await api.post(`/ravim/exames-colab`, { cpf_colaborador: cpf, num_agenda: agenda });
      if (res.status === 200) {
        const formattedExames = res.data.map((ex: any) => ({
          id_exames: String(ex.id_exame_colab),
          nome: ex.exame_empresa
        }));
        
        setCampoValor(formattedExames);
        setOriginalData(prev => ({ 
          ...prev, 
          exames: formattedExames 
        }));
        
        // Dados completamente carregados
        setIsDataLoaded(true);
      }
    } catch (err) { 
      console.error(err);
      setIsDataLoaded(true); // Mesmo com erro, marca como carregado
    }
  };

  // Função para converter File para string (nome do arquivo)
  const getArquivoNome = (arquivo: File | string): string => {
    if (typeof arquivo === 'string') {
      return normalizeArquivo(arquivo);
    }
    return arquivo.name || '';
  };

  // Verificar se houveram alterações - executa sempre que os dados mudam
  useEffect(() => {
    if (isDataLoaded) {
      const dataChanged = dataAgendamento !== originalData.data_agendamento;
      const tipoChanged = tipoAso !== originalData.tipo_aso;
      const examesChanged = JSON.stringify(campoValor) !== JSON.stringify(originalData.exames);
      
      // Comparar arquivos (convertendo File para string quando necessário)
      const arquivoASOAtual = getArquivoNome(arquivoASO);
      const arquivoProntuarioAtual = getArquivoNome(arquivoProntuario);
      
      const arquivoASOChanged = arquivoASOAtual !== originalData.arquivo_aso;
      const arquivoProntuarioChanged = arquivoProntuarioAtual !== originalData.arquivo_prontuario;
      
      const hasAnyChanges = dataChanged || tipoChanged || examesChanged || arquivoASOChanged || arquivoProntuarioChanged;
      
      console.log('=== DEBUG ATUALIZAÇÃO ===');
      console.log('hasAnyChanges:', hasAnyChanges);
      console.log('dataChanged:', dataChanged);
      console.log('tipoChanged:', tipoChanged);
      console.log('examesChanged:', examesChanged);
      console.log('arquivoASOChanged:', arquivoASOChanged, `"${arquivoASOAtual}" !== "${originalData.arquivo_aso}"`);
      console.log('arquivoProntuarioChanged:', arquivoProntuarioChanged);
      console.log('========================');
      
      setHasChanges(hasAnyChanges);
    }
  }, [dataAgendamento, tipoAso, campoValor, arquivoASO, arquivoProntuario, originalData, isDataLoaded]);

  async function comboExames() {
    try {
      const res = await api.get(`/ravim/exames`);
      setNewData(res.data);
    } catch (err) { console.error(err); }
  }

  const valueExames = () => {
    if (singleExame && !campoValor.some(ex => ex.id_exames === singleExame.id_exames)) {
      setCampoValor([...campoValor, singleExame]);
      setSingleExame(null);
    }
  };

  const deleteExame = (idExame: string) => {
    setCampoValor(prevCampoValor => {
      return prevCampoValor.filter(ex => ex.id_exames !== idExame);
    });
  };

  async function sendAgendamento() {
    if (campoValor.length <= 0) {
      setSeverity('warning');
      setMessage('Adicione pelo menos um exame');
      setSnack(true);
      return;
    }

    setIsSaving(true);
    setLoading(true);

    const hora = new Date().toLocaleString('pt-BR', { hour12: false });
    const data = {
      tipo_aso: tipoAso,
      data_agendamento: dataAgendamento,
      tipo_agendamento: getDataSchedule?.tipo_agendamento,
      nome: nomeColab,
      cpf_colaborador: cpfColab,
      num_agenda: getDataSchedule?.num_agenda,
      usuario: `${nome_user} ${hora}`,
      exame: campoValor,
      arquivo_aso: arquivoASO || getDataSchedule?.arquivo_aso,
      arquivo_prontuario: arquivoProntuario || getDataSchedule?.arquivo_prontuario,
    };

    try {
      const res = await api.post(`/ravim/atua-agendamento-externo`, data);
      if (res.status === 200) {
        setSeverity('success');
        setMessage(res.data.message);
        setSnack(true);
        
        if (arquivoSendProntuario || arquivoSendASO) {
          const formData = new FormData();
          if (arquivoSendProntuario) formData.append('arquivo1', arquivoSendProntuario);
          if (arquivoSendASO) formData.append('arquivo2', arquivoSendASO);
          await api.post(`/file`, formData);
        }

        // Atualizar os dados originais com os valores salvos
        setOriginalData({
          data_agendamento: dataAgendamento,
          tipo_aso: tipoAso,
          exames: [...campoValor],
          arquivo_aso: normalizeArquivo(getArquivoNome(arquivoASO) || getDataSchedule?.arquivo_aso || ''),
          arquivo_prontuario: normalizeArquivo(getArquivoNome(arquivoProntuario) || getDataSchedule?.arquivo_prontuario || ''),
        });
        
        setHasChanges(false);
        
        setTimeout(() => {
          setIsSaving(false);
          setLoading(false);
        }, 1500);
      }
    } catch (err: any) {
      setSeverity('error');
      setMessage(err.message || 'Erro ao salvar');
      setSnack(true);
      setIsSaving(false);
      setLoading(false);
    }
  }

  const reqpdf = useCallback(async (tipo: number, cpf_colab: string, cnpj_comer: string, agenda: string, cnpj_emp: string) => {
    try {
      const res = await api.post(`/ravim/pdf`, {
        cpf_colaborador: cpf_colab,
        cnpj_cpf_comer: cnpj_comer,
        num_agenda: agenda,
        cnpj_empresa: cnpj_emp,
      });
      tipo === 1 ? Guia(res.data, 'externo') : PDF(res.data, '');
    } catch (error: any) {
      setSeverity('error');
      setMessage(error.message);
      setSnack(true);
    }
  }, []);

  async function downloadFile(arquivo: string) {
    try {
      const res = await api.post(`/ravim/download`, { arquivo });
      const link = document.createElement('a');
      link.href = res.data.dados;
      link.download = arquivo.replace(/[_]/g, ' ');
      link.click();
    } catch (err) { console.error(err); }
  }

  useEffect(() => {
    comboExames();
  }, []);

  // Mostrar loading enquanto os dados não foram carregados
  if (isFetching || !getDataSchedule || !isDataLoaded) {
    return <LoadingPage open={true} onClick={() => {}} />;
  }

  console.log('=== ESTADO ATUAL DOS BOTÕES ===');
  console.log('hasChanges:', hasChanges);
  console.log('isSaving:', isSaving);
  console.log('Gerar ASO - disabled:', hasChanges || isSaving);
  console.log('Salvar - disabled:', !hasChanges || isSaving);
  console.log('================================');

  return (
    <Container maxWidth="lg" style={{ paddingTop: 30, paddingBottom: 50 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" style={{ fontWeight: 'bold', color: '#1a237e' }}>
          Editar Agendamento Externo
        </Typography>
        <Box display="flex" style={{ gap: 1 }}>
          {verifyUpLoads(getDataSchedule?.arquivo_aso) && (
            <Button title="ASO" icon={<GetApp />} onClick={() => downloadFile(getFileName(getDataSchedule.arquivo_aso))} style={{ minWidth: '150px' }} />
          )}
          {verifyUpLoads(getDataSchedule?.arquivo_prontuario) && (
            <Button title="Prontuário" icon={<GetApp />} onClick={() => downloadFile(getFileName(getDataSchedule.arquivo_prontuario))} style={{ minWidth: '150px' }} />
          )}
          <Button 
            title="Gerar ASO" 
            variantstyle="SECUNDARY" 
            icon={<GetApp />} 
            onClick={() => reqpdf(2, getDataSchedule.cpf_colaborador, getDataSchedule.cpf_cnpj_comer, getDataSchedule.num_agenda, getDataSchedule.cnpj_empresa)} 
            style={{ minWidth: '150px' }}
            disabled={hasChanges || isSaving}
          />
        </Box>
      </Box>

      <Paper elevation={3} style={{ padding: 30, borderRadius: 12 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            
            <SectionTitle>Arquivos e Documentos</SectionTitle>
            <Box display="flex" style={{ gap: 3 }} p={1}>
              <LabelUpload
                title="Upload - ASO"
                variantstyle="DARK_PINK"
                htmlFor="arquivoASO"
                icon={arquivoASO ? <CheckCircle color="primary" /> : <Upload />}
                onChange={(e: any) => {
                  const file = e.target.files[0];
                  if (file) {
                    setArquivoASO(sendFileWithoutFake(e.target.value));
                    setArquivoSendASO(file);
                  }
                }}
              />
              <LabelUpload
                title="Upload - Prontuário"
                variantstyle="LIGHT_PINK"
                htmlFor="arquivoProntuario"
                icon={arquivoProntuario ? <CheckCircle color="primary" /> : <Upload />}
                onChange={(e: any) => {
                  const file = e.target.files[0];
                  if (file) {
                    setArquivoProntuario(sendFileWithoutFake(e.target.value));
                    setArquivoSendProntuario(file);
                  }
                }}
              />
            </Box>

            <SectionTitle>Informações do Colaborador</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField label="CPF" disabled variant="outlined" fullWidth value={cpfColab} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField label="Nome Completo" disabled variant="outlined" fullWidth value={nomeColab} />
              </Grid>
            </Grid>

            <SectionTitle>Dados do Agendamento</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  label="Data do Agendamento" 
                  variant="outlined" 
                  type="date" 
                  fullWidth 
                  InputLabelProps={{ shrink: true }} 
                  value={dataAgendamento} 
                  onChange={(e) => setDataAgendamento(e.target.value)} 
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Tipo de ASO</InputLabel>
                  <Select 
                    label="Tipo de ASO" 
                    value={tipoAso} 
                    onChange={(e: any) => setTipoAso(e.target.value)}
                  >
                    {ASO.map((a, i) => <MenuItem key={i} value={a.value}>{a.value}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <SectionTitle>Exames do Agendamento</SectionTitle>
            <Box mb={2}>
              <Button 
                title="Adicionar Exame" 
                variantstyle="SECUNDARY" 
                icon={<Add />} 
                onClick={() => setDialog(true)} 
                style={{ minWidth: '200px' }} 
              />
            </Box>
            
            <Grid container spacing={2}>
              {campoValor && campoValor.map((ex) => (
                <Grid item xs={12} sm={6} key={ex.id_exames}>
                  <Paper variant="outlined" style={{ padding: '10px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f9f9f9' }}>
                    <Typography variant="body2"><strong>ID:</strong> {ex.id_exames} | <strong>Exame:</strong> {ex.nome}</Typography>
                    <IconButton size="small" onClick={() => deleteExame(ex.id_exames)} color="secondary">
                      <Delete />
                    </IconButton>
                  </Paper>
                </Grid>
              ))}
            </Grid>

          </Grid>
        </Grid>
      </Paper>

      <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: 2 }} mt={4}>
        {getDataSchedule?.token_kit && (
          <LinkKitButton
            token_kit={getDataSchedule.token_kit}
            id_agendamento={getDataSchedule.id_agendamento}
            data_agendamento={getDataSchedule.data_agendamento}
          />
        )}
        <Link to="/agendamentos" style={{ textDecoration: 'none' }}>
          <ButtonSair startIcon={<ArrowBack />} style={{ minWidth: '150px' }}>Sair sem Salvar</ButtonSair>
        </Link>
        <ButtonAdd 
          startIcon={<Save />} 
          onClick={sendAgendamento} 
          style={{ minWidth: '150px' }}
          disabled={!hasChanges || isSaving}
        >
          {isSaving ? 'Salvando...' : 'Salvar Alterações'}
        </ButtonAdd>
      </Box>

      {/* Diálogo de Exames */}
      <Dialog open={dialog} onClose={() => setDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Adicionar Novo Exame</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Autocomplete
              options={newData}
              getOptionLabel={(option) => `${option.id_exames} - ${option.nome}`}
              value={singleExame}
              onChange={(_, newValue) => setSingleExame(newValue)}
              renderInput={(params) => <TextField {...params} variant="outlined" label="Pesquisar Exame" fullWidth />}
              getOptionDisabled={(option) => campoValor.some(ex => ex.nome === option.nome)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <ButtonM onClick={() => setDialog(false)}>Cancelar</ButtonM>
          <ButtonAdd onClick={() => { valueExames(); setDialog(false); }}>Adicionar</ButtonAdd>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack} autoHideDuration={4000} onClose={() => setSnack(false)}>
        <MuiAlert severity={severity} variant="filled" elevation={6}>
          {message}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};